/**
 * Centralized staleness logic for Dashboard data freshness indicators.
 * Thresholds are in days: warning = max age for green, critical = max age for yellow.
 * Beyond critical = red. No data at all = grey.
 */

// Per-module default thresholds (in days) — based on AML compliance expectations
export const MODULE_STALENESS_THRESHOLDS = {
  GeoRisk:               { warning: 90,  critical: 180 }, // Quarterly update expected
  CustomerRisk:          { warning: 30,  critical: 90  }, // Monthly update expected
  BWRA:                  { warning: 90,  critical: 365 }, // Quarterly update expected
  TransactionMonitoring: { warning: 7,   critical: 30  }, // Weekly update expected
  PeriodicReporting:     { warning: 30,  critical: 90  }, // Monthly update expected
};

// Actions that indicate meaningful data changes (exclude LOGIN/LOGOUT)
const DATA_ACTIONS = ['CREATE', 'UPDATE', 'DELETE', 'DATA_IMPORT', 'DATA_EXPORT'];

/**
 * Given a list of audit logs, find the most recent data-change timestamp for a module.
 * @param {string} moduleName  - The module name as used in audit logs (e.g. 'GeoRisk')
 * @param {Array}  logs        - Array of audit log entries
 * @returns {string|null}      - ISO timestamp string or null if no data
 */
export function getModuleLastUpdated(moduleName, logs) {
  const moduleLogs = logs
    .filter(log => log.module === moduleName && DATA_ACTIONS.includes(log.action))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return moduleLogs.length > 0 ? moduleLogs[0].timestamp : null;
}

/**
 * Determine the staleness status based on age and thresholds.
 * @param {string|null} lastUpdated - ISO timestamp or null
 * @param {{ warning: number, critical: number }} thresholds
 * @returns {'green'|'yellow'|'red'|'grey'}
 */
export function getStalenessStatus(lastUpdated, thresholds) {
  if (!lastUpdated) return 'grey';
  const ageDays = getAgeDays(lastUpdated);
  if (ageDays === null) return 'grey';
  if (ageDays <= thresholds.warning) return 'green';
  if (ageDays <= thresholds.critical) return 'yellow';
  return 'red';
}

/**
 * Get the age in whole days from an ISO timestamp to now.
 * @param {string|null} lastUpdated
 * @returns {number|null}
 */
export function getAgeDays(lastUpdated) {
  if (!lastUpdated) return null;
  try {
    const ageMs = Date.now() - new Date(lastUpdated).getTime();
    return Math.floor(ageMs / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}
